import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, User } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ReferralCodeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  referralCode: string;
  practitionerName: string;
  discountPercentage?: number;
  onSuccess: () => void;
}

export const ReferralCodeModal: React.FC<ReferralCodeModalProps> = ({
  open,
  onOpenChange,
  referralCode,
  practitionerName,
  discountPercentage,
  onSuccess,
}) => {
  const [isApplying, setIsApplying] = useState(false);
  const { toast } = useToast();

  const handleApply = async () => {
    setIsApplying(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/referrals/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: referralCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to apply referral code');
      }

      toast({
        title: 'Referral Code Applied!',
        description: `You're now connected with ${practitionerName}. ${
          discountPercentage ? `You'll receive ${discountPercentage}% off all sessions!` : ''
        }`,
      });

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Failed to Apply Referral Code',
        description: error.message || 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-primary" />
            Apply Referral Code
          </DialogTitle>
          <DialogDescription className="pt-4">
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground">{practitionerName}</p>
                  <p className="text-sm text-muted-foreground">Practitioner</p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  You've been invited to join VoiceThera by <span className="font-semibold text-foreground">{practitionerName}</span>.
                </p>
                {discountPercentage && discountPercentage > 0 && (
                  <div className="p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <p className="text-sm font-medium text-green-800 dark:text-green-200">
                      Special Offer: {discountPercentage}% off all sessions!
                    </p>
                  </div>
                )}
                <p className="text-sm text-muted-foreground">
                  Would you like to connect with this practitioner?
                </p>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isApplying}
            className="w-full sm:w-auto"
          >
            Not Now
          </Button>
          <Button
            onClick={handleApply}
            disabled={isApplying}
            className="w-full sm:w-auto"
          >
            {isApplying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Applying...
              </>
            ) : (
              'Apply Referral Code'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
